import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, getDocs, getDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Emogiface, Emogiface2, Emogiface3, Emogiface4, Emogiface5, Emogiface6 } from '../../../tools/MyIcons';

// ── Exportación a PNG ─────────────────────────────────────────────────────────
// npm install html2canvas
import html2canvas from 'html2canvas';

// ── ECharts ──────────────────────────────────────────────────────────────────
// npm install echarts echarts-for-react
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent, TooltipComponent, TitleComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import ReactECharts from 'echarts-for-react/lib/core';

import {
  ThemeProvider, createTheme, CssBaseline,
  Box, Typography, Card, CardContent, Chip,
  Grid, Stack, CircularProgress,
  Alert, Fade, Grow, Button, Collapse, Divider,
  Avatar, IconButton, Tooltip,
} from '@mui/material';
import BarChartIcon           from '@mui/icons-material/BarChart';
import PeopleAltIcon          from '@mui/icons-material/PeopleAlt';
import StarRoundedIcon        from '@mui/icons-material/StarRounded';
import QuizIcon               from '@mui/icons-material/Quiz';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import TuneIcon               from '@mui/icons-material/Tune';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TextFieldsIcon         from '@mui/icons-material/TextFields';
import ChatBubbleOutlineIcon  from '@mui/icons-material/ChatBubbleOutline';
import CommentIcon            from '@mui/icons-material/Comment';
import ExpandMoreIcon         from '@mui/icons-material/ExpandMore';
import ExpandLessIcon         from '@mui/icons-material/ExpandLess';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import AccessTimeIcon         from '@mui/icons-material/AccessTime';
import WbSunnyRoundedIcon     from '@mui/icons-material/WbSunnyRounded';
import DarkModeRoundedIcon    from '@mui/icons-material/DarkModeRounded';
import SortIcon               from '@mui/icons-material/Sort';
import FilterListIcon         from '@mui/icons-material/FilterList';
import ImageOutlinedIcon      from '@mui/icons-material/ImageOutlined';
import DownloadRoundedIcon    from '@mui/icons-material/DownloadRounded';

echarts.use([BarChart, GridComponent, TooltipComponent, TitleComponent, CanvasRenderer]);

// ─── i18n ─────────────────────────────────────────────────────────────────────
const TRANSLATIONS = {
  en: {
    filterAll: 'All',
    statResponses:  'Responses',
    statAverage:    'Global avg.',
    statQuestions:  'Questions',
    typeRating:       'Rating',
    typeEmoji:        'Emoji',
    typeEmojiCat:     'Emoji Cat',
    typeEmojiPeople:  'Emoji People',
    typeRadio:        'Multiple choice',
    typeSlider:       'Range',
    typeText:         'Free text',
    resp:             'resp.',
    textAnswersNote:  'Individual answers in the',
    textAnswersNoteSuffix: 'collection.',
    typeUnsupported:  "Type not supported",
    commentsEnabled:   'Comments enabled',
    commentsDisabled:  'Comments disabled',
    commentsEnabledDesc:  'Participants can leave comments on this survey.',
    commentsDisabledDesc: 'The comments option is disabled for this survey.',
    showComments:      'View comments',
    hideComments:      'Hide comments',
    noComments:        'No comments in this period.',
    loading:           'Loading results…',
    errorSurvey:       'Survey not found.',
    errorGeneric:      'Could not load results.',
    noData:            'No responses yet for this survey.',
    surveyTitle:       'Survey Results',
    toggleLight:       'Switch to light mode',
    toggleDark:        'Switch to dark mode',
    sortByOrder:       'Original order',
    sortByResult:      'Sort by result',
    sortTooltip:       'Sort questions by most-selected answer (1 → 5)',
    dominantLabel:     'Most selected',
    exportQuestion:    'Download question as PNG',
    exportChart:       'Download chart as PNG',
    months: ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
  },
  es: {
    filterAll: 'Todo',
    statResponses:  'Respuestas',
    statAverage:    'Promedio global',
    statQuestions:  'Preguntas',
    typeRating:       'Calificación',
    typeEmoji:        'Emoji',
    typeEmojiCat:     'Emoji Cat',
    typeEmojiPeople:  'Emoji People',
    typeRadio:        'Múltiple',
    typeSlider:       'Rango',
    typeText:         'Texto libre',
    resp:             'resp.',
    textAnswersNote:  'Respuestas individuales en la colección',
    textAnswersNoteSuffix: '.',
    typeUnsupported:  "Tipo no soportado",
    commentsEnabled:   'Comentarios activados',
    commentsDisabled:  'Comentarios desactivados',
    commentsEnabledDesc:  'Los participantes pueden dejar comentarios en este survey.',
    commentsDisabledDesc: 'La opción de comentarios está deshabilitada para este survey.',
    showComments:      'Ver comentarios',
    hideComments:      'Ocultar comentarios',
    noComments:        'No hay comentarios en este período.',
    loading:           'Cargando resultados…',
    errorSurvey:       'Survey no encontrado.',
    errorGeneric:      'No se pudieron cargar los resultados.',
    noData:            'Aún no hay respuestas para este survey.',
    surveyTitle:       'Resultados del Survey',
    toggleLight:       'Cambiar a modo claro',
    toggleDark:        'Cambiar a modo oscuro',
    sortByOrder:       'Orden original',
    sortByResult:      'Ordenar por resultado',
    sortTooltip:       'Ordena las preguntas por la respuesta más seleccionada (1 → 5)',
    dominantLabel:     'Más seleccionado',
    exportQuestion:    'Descargar pregunta como PNG',
    exportChart:       'Descargar gráfico como PNG',
    months: ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'],
  },
};

const detectLang = () => {
  const lang = (navigator?.language || navigator?.languages?.[0] || 'en').toLowerCase();
  return lang.startsWith('es') ? 'es' : 'en';
};

const useTranslations = () => {
  const lang = useMemo(() => detectLang(), []);
  return TRANSLATIONS[lang] ?? TRANSLATIONS.en;
};

// ─── Palette helper ───────────────────────────────────────────────────────────
const getPalette = (mode) => {
  const isDark = mode === 'dark';
  return {
    isDark,
    pageBg: isDark
      ? ['radial-gradient(ellipse at 15% 0%, rgba(108,99,255,0.13) 0%, transparent 50%)',
         'radial-gradient(ellipse at 85% 100%, rgba(255,101,132,0.09) 0%, transparent 50%)',
         '#0D0F1A'].join(', ')
      : ['radial-gradient(ellipse at 15% 0%, rgba(108,99,255,0.07) 0%, transparent 50%)',
         'radial-gradient(ellipse at 85% 100%, rgba(255,101,132,0.05) 0%, transparent 50%)',
         '#F4F5FF'].join(', '),
    paperBg:  isDark ? '#151828' : '#FFFFFF',
    cardBg:   (accent) => isDark
      ? `linear-gradient(160deg, ${accent}14 0%, #151828 55%)`
      : `linear-gradient(160deg, ${accent}0C 0%, #FFFFFF 60%)`,
    statCardBg: (color) => isDark
      ? `linear-gradient(135deg, ${color}22 0%, #15182800 70%)`
      : `linear-gradient(135deg, ${color}14 0%, #FFFFFF00 70%)`,
    statCardBorder: (color) => isDark
      ? `1px solid ${color}38 !important`
      : `1px solid ${color}50 !important`,
    commentText: isDark ? '#EEF0FF' : '#1A1C2E',
    timeColor:   isDark ? '#4A4F6A' : '#9CA3AF',
    commentBoxBg:     isDark ? 'rgba(21,24,40,0.7)'      : 'rgba(255,255,255,0.92)',
    commentBoxBorder: isDark ? '1px solid rgba(108,99,255,0.22)' : '1px solid rgba(108,99,255,0.15)',
    commentBubbleBg:  isDark ? 'rgba(255,255,255,0.04)'   : 'rgba(0,0,0,0.025)',
    commentBubbleBorder: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.07)',
    commentBubbleHover:  isDark ? 'rgba(108,99,255,0.07)' : 'rgba(108,99,255,0.05)',
    timelineLineGrad: isDark
      ? 'linear-gradient(to bottom, #6C63FF66, transparent)'
      : 'linear-gradient(to bottom, #6C63FF44, transparent)',
    progressTrack: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)',
    miscChipBg:   isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    dividerColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.09)',
    gridLine:     isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    barTrack:     isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    ratingBadgeBg:     isDark ? 'rgba(249,199,79,0.1)'  : 'rgba(249,199,79,0.12)',
    ratingBadgeBorder: isDark ? '1px solid rgba(249,199,79,0.3)' : '1px solid rgba(249,199,79,0.4)',
    sliderBadgeBg:     isDark ? 'rgba(108,99,255,0.1)'  : 'rgba(108,99,255,0.08)',
    sliderBadgeBorder: isDark ? '1px solid rgba(108,99,255,0.3)' : '1px solid rgba(108,99,255,0.3)',
    headerLogoBg: 'linear-gradient(135deg, #6C63FF, #FF6584)',
    toggleBg:     isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
    toggleHover:  isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
    toggleColor:  isDark ? '#F9C74F' : '#6C63FF',
    axisLabelColor:  isDark ? '#8B90B8' : '#6B7280',
    valueLabelColor: isDark ? '#EEF0FF' : '#1A1C2E',
    exportGroupBg:     isDark ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.75)',
    exportGroupShadow: isDark ? '0 2px 10px rgba(0,0,0,0.28)' : '0 2px 10px rgba(108,99,255,0.10)',
  };
};

// ─── Theme factory ────────────────────────────────────────────────────────────
const buildTheme = (mode) => createTheme({
  palette: {
    mode,
    primary:    { main: '#6C63FF' },
    secondary:  { main: '#FF6584' },
    success:    { main: '#43E97B' },
    warning:    { main: '#F9C74F' },
    error:      { main: '#FF6B6B' },
    background: mode === 'dark'
      ? { default: '#0D0F1A', paper: '#151828' }
      : { default: '#F4F5FF', paper: '#FFFFFF'  },
    text: mode === 'dark'
      ? { primary: '#EEF0FF', secondary: '#8B90B8' }
      : { primary: '#1A1C2E', secondary: '#6B7280' },
  },
  typography: {
    fontFamily: "'Outfit', 'Segoe UI', sans-serif",
    h5:        { fontWeight: 800, letterSpacing: '-0.02em' },
    subtitle2: { fontWeight: 600 },
    caption:   { letterSpacing: '0.06em' },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: mode === 'dark'
            ? '1px solid rgba(108,99,255,0.12)'
            : '1px solid rgba(108,99,255,0.14)',
          boxShadow: mode === 'dark'
            ? 'none'
            : '0 1px 8px rgba(108,99,255,0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { transition: 'all 0.2s' } },
    },
  },
});

// ─── Emoji sets ───────────────────────────────────────────────────────────────
const EMOJI_SETS = {
  emojis:        Emogiface,
  emojis2:       Emogiface2,
  emojisCat:     Emogiface3,
  emojispeople:  Emogiface4,
  emojisnumbers: Emogiface5,
  emojislike:    Emogiface6,
};

const getEmojiSrc = (type, val) => EMOJI_SETS[type]?.[`face${val}`] || null;

// ─── Constants ────────────────────────────────────────────────────────────────
const RATING_GRADIENT = ['#FF6B6B', '#FF9F43', '#F9C74F', '#A8E063', '#43E97B'];
const RADIO_COLORS    = ['#6C63FF', '#FF6584', '#43E97B', '#F9C74F', '#38BDF8'];

const CARD_ACCENT = {
  rating:        '#6C63FF',
  emojis:        '#FF6584',
  emojis2:       '#FF6584',
  emojisCat:     '#FF6584',
  emojispeople:  '#FF6584',
  emojisnumbers: '#FF6584',
  emojislike:    '#FF6584',
  radio:         '#43E97B',
  slider:        '#38BDF8',
  text:          '#8B90B8',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const periodKeyFromData = (data) => {
  const anio  = data.anio ?? '';
  const month = String(data.month ?? '').padStart(2, '0');
  return `${anio}_${month}`;
};

const periodLabel = (key, months) => {
  const [year, month] = key.split('_');
  return `${months[parseInt(month, 10)]} ${year}`;
};

const currentPeriodKey = () => {
  const now = new Date();
  return `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  const lang = detectLang();
  return d.toLocaleString(lang === 'es' ? 'es-MX' : 'en-US', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const uidInitials = (uid = '') => uid.slice(-2).toUpperCase();
const stringColor = (str = '') => {
  const palette = ['#6C63FF','#FF6584','#43E97B','#F9C74F','#38BDF8','#FB923C','#A78BFA'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
};

const aggregateGroups = (surveyQuestions, sumDocs) => {
  const meta = {};
  Object.values(surveyQuestions || {}).forEach((q) => {
    const o = q.orden ?? 0;
    if (!meta[o]) meta[o] = q;
  });
  const acc = {};
  sumDocs.forEach((d) => {
    const o = d.orden ?? 0;
    if (!acc[o]) {
      acc[o] = {
        orden: o, text: d.text || meta[o]?.text || `Question ${o}`,
        type: meta[o]?.type || 'rating', options: meta[o]?.options || [],
        sliderConfig: meta[o]?.sliderConfig || null,
        url: meta[o]?.url || '',
        counts: { 1:0, 2:0, 3:0, 4:0, 5:0 },
      };
    }
    for (let i = 1; i <= 5; i++) acc[o].counts[i] += d[`ans_${i}`] || 0;
  });
  return Object.values(acc)
    .map((g) => ({ ...g, total: Object.values(g.counts).reduce((s,n)=>s+n,0) }))
    .sort((a, b) => a.orden - b.orden);
};

const weightedAvg = (counts, total) => {
  if (!total) return null;
  const sum = Object.entries(counts).reduce((s,[k,v]) => s + Number(k)*v, 0);
  return (sum / total).toFixed(2);
};

const dominantAnswer = (counts) => {
  let best = 99, bestCount = 0;
  for (let i = 1; i <= 5; i++) {
    const c = counts[i] || 0;
    if (c > bestCount || (c === bestCount && c > 0 && i < best)) {
      bestCount = c; best = i;
    }
  }
  return best;
};

// ─── Exportación a PNG ────────────────────────────────────────────────────────
const sanitizeFilename = (str = '') => str
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .toLowerCase()
  .slice(0, 24) || 'pregunta';

const downloadDataUrl = (dataUrl, filename) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ─── SortBar ──────────────────────────────────────────────────────────────────
const SortBar = ({ sortMode, onChange, t, p }) => (
  <Tooltip title={t.sortTooltip} placement="top">
    <Stack
      direction="row"
      spacing={0}
      sx={{
        borderRadius: 2, overflow: 'hidden', flexShrink: 0,
        border: `1px solid ${p.isDark ? 'rgba(108,99,255,0.25)' : 'rgba(108,99,255,0.22)'}`,
      }}
    >
      {[
        { key: 'orden',  icon: <FilterListIcon sx={{ fontSize: 14 }} />, label: t.sortByOrder  },
        { key: 'result', icon: <SortIcon       sx={{ fontSize: 14 }} />, label: t.sortByResult },
      ].map(({ key, icon, label }) => {
        const active = sortMode === key;
        return (
          <Box
            key={key}
            onClick={() => onChange(key)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: { xs: 1, sm: 1.4 }, py: 0.7,
              cursor: 'pointer',
              fontSize: '0.7rem', fontWeight: active ? 700 : 500,
              color: active ? '#fff' : 'text.secondary',
              background: active
                ? 'linear-gradient(135deg, #6C63FF, #9C63FF)'
                : p.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              transition: 'all 0.2s',
              userSelect: 'none', whiteSpace: 'nowrap',
              '&:hover': !active ? {
                background: p.isDark ? 'rgba(108,99,255,0.14)' : 'rgba(108,99,255,0.08)',
                color: '#6C63FF',
              } : {},
            }}
          >
            {icon}
            <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  </Tooltip>
);

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color, delay = 0, p }) => (
  <Fade in timeout={600} style={{ transitionDelay: `${delay}ms` }}>
    <Card sx={{
      flex: 1, minWidth: 0,
      background: p.statCardBg(color),
      border: p.statCardBorder(color),
      transition: 'transform 0.2s',
      '&:hover': { transform: 'translateY(-2px)' },
    }}>
      <CardContent sx={{ p: { xs: '12px !important', sm: '18px !important' } }}>
        <Stack direction="row" alignItems="center" spacing={0.8} mb={0.5}>
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Typography variant="caption" color="text.secondary"
            sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            {label}
          </Typography>
        </Stack>
        <Typography sx={{ color, fontWeight: 800, fontSize: { xs: '1.5rem', sm: '1.9rem' }, lineHeight: 1 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  </Fade>
);

// ─── StarRow (used in rating average badge) ───────────────────────────────────
const StarRow = ({ filled }) => (
  <Stack direction="row" spacing={0.2}>
    {Array.from({ length: 5 }, (_, i) => (
      <StarRoundedIcon key={i} sx={{
        fontSize: 13,
        color: i < filled ? '#F9C74F' : 'rgba(128,128,128,0.2)',
      }} />
    ))}
  </Stack>
);

// ─── ECharts: Vertical bar chart (rating / slider / emoji) ───────────────────
// bars: [{ label, value, pct, color, imgSrc? }]
const EChartsVerticalBar = ({ bars, p, chartRef }) => {
  const hasImages = bars.some((b) => b.imgSrc);

  const option = useMemo(() => {
    const rich = {};
    bars.forEach((b, i) => {
      if (b.imgSrc) {
        rich[`img${i}`] = {
          height: 22,
          backgroundColor: { image: b.imgSrc },
        };
      }
      rich[`lbl${i}`] = {
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 16,
        color: b.value > 0 ? b.color : p.axisLabelColor,
      };
      rich[`pct${i}`] = {
        fontSize: 9,
        lineHeight: 14,
        color: p.axisLabelColor,
      };
    });

    return {
      backgroundColor: 'transparent',
      grid: { left: 6, right: 6, top: 26, bottom: hasImages ? 40 : 32, containLabel: false },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: p.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' } },
        backgroundColor: p.paperBg,
        borderColor: p.dividerColor,
        textStyle: { color: p.valueLabelColor, fontSize: 12 },
        formatter: (params) => {
          const idx = params[0].dataIndex;
          const b = bars[idx];
          return `${b.label}: <b>${b.value}</b> (${b.pct}%)`;
        },
      },
      xAxis: {
        type: 'category',
        data: bars.map((_, i) => String(i)),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: p.dividerColor } },
        axisLabel: {
          interval: 0,
          margin: 10,
          formatter: (val, idx) => {
            const b = bars[idx];
            const top = b.imgSrc ? `{img${idx}|}` : `{lbl${idx}|${b.label}}`;
            return `${top}\n{pct${idx}|${b.pct > 0 ? b.pct + '%' : '–'}}`;
          },
          rich,
        },
      },
      yAxis: { type: 'value', show: false },
      series: [{
        type: 'bar',
        data: bars.map((b) => ({
          value: b.value,
          itemStyle: {
            color: b.value > 0
              ? new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: b.color },
                  { offset: 1, color: `${b.color}88` },
                ])
              : p.barTrack,
            borderRadius: [5, 5, 0, 0],
          },
        })),
        barMaxWidth: 40,
        label: {
          show: true,
          position: 'top',
          fontSize: 11,
          fontWeight: 800,
          formatter: ({ value }) => (value > 0 ? value : ''),
          color: p.valueLabelColor,
        },
        emphasis: { itemStyle: { opacity: 0.85 } },
        animationDuration: 550,
        animationEasing: 'elasticOut',
      }],
    };
  }, [bars, p, hasImages]);

  return (
    <ReactECharts
      ref={chartRef}
      echarts={echarts}
      option={option}
      notMerge
      lazyUpdate
      style={{ width: '100%', height: 150 }}
    />
  );
};

// ─── ECharts: Horizontal bar chart (radio / multiple choice) ─────────────────
// bars: [{ label, value, pct, color }]
const EChartsHorizontalBar = ({ bars, p, chartRef }) => {
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    grid: { left: 8, right: 64, top: 6, bottom: 6, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: p.paperBg,
      borderColor: p.dividerColor,
      textStyle: { color: p.valueLabelColor, fontSize: 12 },
      formatter: (params) => {
        const idx = params[0].dataIndex;
        const b = bars[idx];
        return `${b.label}: <b>${b.value}</b> (${b.pct}%)`;
      },
    },
    xAxis: { type: 'value', show: false },
    yAxis: {
      type: 'category',
      inverse: true,
      data: bars.map((b) => b.label),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 12,
        color: p.axisLabelColor,
        width: 130,
        overflow: 'truncate',
      },
    },
    series: [{
      type: 'bar',
      data: bars.map((b) => ({
        value: b.value,
        itemStyle: {
          color: b.value > 0 ? b.color : p.barTrack,
          borderRadius: [0, 5, 5, 0],
        },
      })),
      barMaxWidth: 22,
      label: {
        show: true,
        position: 'right',
        fontWeight: 700,
        fontSize: 11,
        color: p.valueLabelColor,
        formatter: ({ dataIndex }) => `${bars[dataIndex].value} (${bars[dataIndex].pct}%)`,
      },
      animationDuration: 550,
      animationEasing: 'elasticOut',
    }],
  }), [bars, p]);

  const height = Math.max(bars.length * 40, 90);

  return (
    <ReactECharts
      ref={chartRef}
      echarts={echarts}
      option={option}
      notMerge
      lazyUpdate
      style={{ width: '100%', height }}
    />
  );
};

// ─── Rating viz ───────────────────────────────────────────────────────────────
const RatingViz = ({ counts, total, p, chartRef }) => {
  const average = weightedAvg(counts, total);

  const bars = [1, 2, 3, 4, 5].map((star) => ({
    label: `★${star}`,
    value: counts[star] || 0,
    pct:   total ? Math.round(((counts[star] || 0) / total) * 100) : 0,
    color: RATING_GRADIENT[star - 1],
  }));

  return (
    <Box>
      <EChartsVerticalBar bars={bars} p={p} chartRef={chartRef} />
      {average && (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.8,
          mt: 1.5, px: 1.5, py: 0.8,
          background: p.ratingBadgeBg, border: p.ratingBadgeBorder, borderRadius: 3,
        }}>
          <StarRow filled={Math.round(parseFloat(average))} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#F9C74F' }}>{average}</Typography>
          <Typography variant="caption" color="text.secondary">/ 5</Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Slider viz ───────────────────────────────────────────────────────────────
const SliderViz = ({ counts, total, sliderConfig, p, chartRef }) => {
  const min = sliderConfig?.min ?? 1;
  const max = sliderConfig?.max ?? 5;
  const average = weightedAvg(counts, total);
  const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  const bars = steps.map((val) => {
    const colorIdx = Math.round(((val - min) / Math.max(max - min, 1)) * (RATING_GRADIENT.length - 1));
    return {
      label: String(val),
      value: counts[val] || 0,
      pct:   total ? Math.round(((counts[val] || 0) / total) * 100) : 0,
      color: RATING_GRADIENT[colorIdx],
    };
  });

  return (
    <Box>
      <EChartsVerticalBar bars={bars} p={p} chartRef={chartRef} />
      {average && (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.8,
          mt: 1.5, px: 1.5, py: 0.8,
          background: p.sliderBadgeBg, border: p.sliderBadgeBorder, borderRadius: 3,
        }}>
          <TuneIcon sx={{ color: '#6C63FF', fontSize: 15 }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#6C63FF' }}>{average}</Typography>
          <Typography variant="caption" color="text.secondary">/ {max}</Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Emoji viz ────────────────────────────────────────────────────────────────
const EmojiViz = ({ counts, total, type, p, chartRef }) => {
  const emojiSet = EMOJI_SETS[type] || {};
  const rangeMax = Object.keys(emojiSet).length || 5;

  const bars = Array.from({ length: rangeMax }, (_, i) => i + 1).map((val) => ({
    label:  String(val),
    value:  counts[val] || 0,
    pct:    total ? Math.round(((counts[val] || 0) / total) * 100) : 0,
    color:  RATING_GRADIENT[Math.round(((val - 1) / Math.max(rangeMax - 1, 1)) * (RATING_GRADIENT.length - 1))],
    imgSrc: getEmojiSrc(type, val),
  }));

  return <EChartsVerticalBar bars={bars} p={p} chartRef={chartRef} />;
};

// ─── Radio viz ────────────────────────────────────────────────────────────────
const RadioViz = ({ counts, total, options, p, chartRef }) => {
  const rows = options.length > 0
    ? options.map((o, i) => {
        const slot  = parseInt(o.id ?? i, 10);
        const count = counts[slot] || 0;
        return {
          key:   String(o.id ?? i),
          label: o.text || `Option ${i + 1}`,
          value: count,
          pct:   total ? Math.round((count / total) * 100) : 0,
          color: RADIO_COLORS[i % RADIO_COLORS.length],
        };
      })
    : [1, 2, 3, 4, 5].map((n) => ({
        key:   String(n),
        label: `Option ${n}`,
        value: counts[n] || 0,
        pct:   total ? Math.round(((counts[n] || 0) / total) * 100) : 0,
        color: RADIO_COLORS[(n - 1) % RADIO_COLORS.length],
      }));

  return <EChartsHorizontalBar bars={rows} p={p} chartRef={chartRef} />;
};

// ─── Export button group (rediseño) ───────────────────────────────────────────
// Píldora compacta con fondo cristalizado, separador entre acciones y acento
// del color de la tarjeta en vez del gris genérico anterior.
const ExportButtonGroup = ({
  onExportCard, onExportChart, exportingCard, exportingChart,
  showChart, accent, p, t,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    data-html2canvas-ignore="true"
    sx={{
      borderRadius: '10px',
      overflow: 'hidden',
      border: `1px solid ${accent}35`,
      background: p.exportGroupBg,
      boxShadow: p.exportGroupShadow,
      backdropFilter: 'blur(6px)',
    }}
  >
    <Tooltip title={t.exportQuestion}>
      <span>
        <IconButton
          size="small"
          onClick={onExportCard}
          disabled={exportingCard}
          sx={{
            width: 30, height: 30, borderRadius: 0,
            color: accent,
            transition: 'transform 0.18s, background 0.18s',
            '&:hover': { background: `${accent}20`, transform: 'scale(1.1)' },
            '&:active': { transform: 'scale(0.94)' },
          }}
        >
          {exportingCard
            ? <CircularProgress size={14} thickness={5} sx={{ color: accent }} />
            : <DownloadRoundedIcon sx={{ fontSize: 16 }} />
          }
        </IconButton>
      </span>
    </Tooltip>

    {showChart && (
      <>
        <Divider orientation="vertical" flexItem sx={{ borderColor: `${accent}30`, my: 0.6 }} />
        <Tooltip title={t.exportChart}>
          <span>
            <IconButton
              size="small"
              onClick={onExportChart}
              disabled={exportingChart}
              sx={{
                width: 30, height: 30, borderRadius: 0,
                color: accent,
                transition: 'transform 0.18s, background 0.18s',
                '&:hover': { background: `${accent}20`, transform: 'scale(1.1)' },
                '&:active': { transform: 'scale(0.94)' },
              }}
            >
              {exportingChart
                ? <CircularProgress size={14} thickness={5} sx={{ color: accent }} />
                : <ImageOutlinedIcon sx={{ fontSize: 16 }} />
              }
            </IconButton>
          </span>
        </Tooltip>
      </>
    )}
  </Stack>
);

// ─── QuestionResult ───────────────────────────────────────────────────────────
const QuestionResult = ({ group, index, delay, t, p }) => {
  const { text, type, counts, total, options, sliderConfig, url } = group;
  const accent = CARD_ACCENT[type] || '#6C63FF';

  const cardRef  = useRef(null);
  const chartRef = useRef(null);
  const [exportingCard, setExportingCard]   = useState(false);
  const [exportingChart, setExportingChart] = useState(false);

  const baseFilename = useMemo(
    () => `q${index + 1}-${sanitizeFilename(text)}`,
    [index, text],
  );

  const handleExportChart = useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;
    setExportingChart(true);
    try {
      const dataUrl = instance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: p.paperBg,
      });
      downloadDataUrl(dataUrl, `${baseFilename}-grafico.png`);
    } finally {
      setExportingChart(false);
    }
  }, [baseFilename, p.paperBg]);

  const handleExportCard = useCallback(async () => {
    if (!cardRef.current) return;
    setExportingCard(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: p.paperBg,
        scale: 2,
        useCORS: true,
      });
      downloadDataUrl(canvas.toDataURL('image/png'), `${baseFilename}.png`);
    } catch (err) {
      console.error('Error exporting question as PNG:', err);
    } finally {
      setExportingCard(false);
    }
  }, [baseFilename, p.paperBg]);

  const dominant = useMemo(() => {
    if (type === 'text') return null;
    const d = dominantAnswer(counts);
    return d <= 5 ? d : null;
  }, [counts, type]);

  const dominantColor = dominant != null ? RATING_GRADIENT[dominant - 1] : null;

  const TYPE_LABELS = {
    rating:        t.typeRating,
    emojis:        t.typeEmoji,
    emojis2:       t.typeEmoji,
    emojisCat:     t.typeEmojiCat,
    emojispeople:  t.typeEmojiPeople,
    emojisnumbers: t.typeEmoji,
    emojislike:    t.typeEmoji,
    radio:         t.typeRadio,
    slider:        t.typeSlider,
    text:          t.typeText,
  };

  const TYPE_ICONS = {
    rating:        <StarRoundedIcon sx={{ fontSize: 13 }} />,
    emojis:        <SentimentSatisfiedIcon sx={{ fontSize: 13 }} />,
    emojis2:       <SentimentSatisfiedIcon sx={{ fontSize: 13 }} />,
    emojisCat:     <SentimentSatisfiedIcon sx={{ fontSize: 13 }} />,
    emojispeople:  <SentimentSatisfiedIcon sx={{ fontSize: 13 }} />,
    emojisnumbers: <SentimentSatisfiedIcon sx={{ fontSize: 13 }} />,
    emojislike:    <SentimentSatisfiedIcon sx={{ fontSize: 13 }} />,
    radio:         <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />,
    slider:        <TuneIcon sx={{ fontSize: 13 }} />,
    text:          <TextFieldsIcon sx={{ fontSize: 13 }} />,
  };

  const renderViz = () => {
    switch (type) {
      case 'rating':
        return <RatingViz counts={counts} total={total} p={p} chartRef={chartRef} />;
      case 'emojis':
      case 'emojis2':
      case 'emojisCat':
      case 'emojispeople':
      case 'emojisnumbers':
      case 'emojislike':
        return <EmojiViz counts={counts} total={total} type={type} p={p} chartRef={chartRef} />;
      case 'radio':
        return <RadioViz counts={counts} total={total} options={options || []} p={p} chartRef={chartRef} />;
      case 'slider':
        return <SliderViz counts={counts} total={total} sliderConfig={sliderConfig} p={p} chartRef={chartRef} />;
      case 'text':
        return (
          <Alert severity="info" sx={{ fontSize: '0.75rem', borderRadius: 2 }}>
            {t.textAnswersNote} <code>answers</code>{t.textAnswersNoteSuffix}
          </Alert>
        );
      default:
        return (
          <Typography variant="caption" color="text.secondary">
            {t.typeUnsupported}: '{type}'
          </Typography>
        );
    }
  };

  return (
    <Grow in timeout={500} style={{ transitionDelay: `${delay}ms` }}>
      <Card ref={cardRef} sx={{
        height: '100%',
        background: p.cardBg(accent),
        borderTop: `3px solid ${accent} !important`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 14px 40px ${accent}28` },
      }}>
        <CardContent sx={{ p: { xs: '14px !important', sm: '20px !important' } }}>

          {/* Top badges */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={0.5}>
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <Box sx={{ px: 1, py: 0.3, borderRadius: 1, background: `${accent}22` }}>
                <Typography variant="caption" sx={{ color: accent, fontWeight: 800, fontSize: '0.62rem' }}>Q{index + 1}</Typography>
              </Box>
              <ExportButtonGroup
                onExportCard={handleExportCard}
                onExportChart={handleExportChart}
                exportingCard={exportingCard}
                exportingChart={exportingChart}
                showChart={type !== 'text'}
                accent={accent}
                p={p}
                t={t}
              />
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.4}>
              {dominant != null && (
                <Chip
                  label={`${t.dominantLabel}: ${dominant}`}
                  size="small"
                  sx={{
                    fontSize: '0.6rem', height: 20,
                    background: `${dominantColor}22`, color: dominantColor,
                    border: `1px solid ${dominantColor}50`, fontWeight: 700,
                  }}
                />
              )}
              <Chip icon={TYPE_ICONS[type]} label={TYPE_LABELS[type] || type} size="small"
                sx={{ fontSize: '0.6rem', height: 20, background: `${accent}18`, color: accent,
                  border: `1px solid ${accent}30`, '& .MuiChip-icon': { color: accent } }} />
              <Chip label={`${total} ${t.resp}`} size="small"
                sx={{ fontSize: '0.6rem', height: 20, background: p.miscChipBg, color: 'text.secondary' }} />
            </Stack>
          </Stack>

          {/* Question text */}
          <Typography variant="subtitle2" sx={{ mb: url ? 1.5 : 2, lineHeight: 1.45, fontSize: { xs: '0.86rem', sm: '0.95rem' } }}>
            {text}
          </Typography>

          {/* Optional image */}
          {url && (
            <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', lineHeight: 0, border: `1px solid ${accent}22` }}>
              <Box component="img" src={url} alt={text} sx={{
                width: '100%', maxHeight: { xs: 160, sm: 200 },
                objectFit: 'cover', display: 'block',
                transition: 'transform 0.3s',
                '&:hover': { transform: 'scale(1.02)' },
              }} />
            </Box>
          )}

          {/* Chart visualization */}
          {renderViz()}

        </CardContent>
      </Card>
    </Grow>
  );
};

// ─── Period filter ────────────────────────────────────────────────────────────
// El chip "Todo" solo aparece cuando hay más de un período disponible.
const FilterBar = ({ periods, selected, onChange, t }) => {
  const showAllOption = periods.length > 1;
  const items = showAllOption
    ? [{ key: 'all', label: t.filterAll }, ...periods.map((pk) => ({ key: pk, label: periodLabel(pk, t.months) }))]
    : periods.map((pk) => ({ key: pk, label: periodLabel(pk, t.months) }));

  return (
    <Box sx={{
      display: 'flex', gap: 0.8, flexWrap: 'nowrap', overflowX: 'auto', pb: 1, mb: 3,
      '&::-webkit-scrollbar': { height: 3 },
      '&::-webkit-scrollbar-thumb': { background: 'rgba(108,99,255,0.45)', borderRadius: 2 },
    }}>
      {items.map(({ key, label }) => (
        <Chip key={key} label={label} onClick={() => onChange(key)}
          variant={selected === key ? 'filled' : 'outlined'}
          color={selected === key ? 'primary' : 'default'}
          sx={{ flexShrink: 0, fontWeight: selected === key ? 700 : 500, fontSize: '0.78rem' }} />
      ))}
    </Box>
  );
};

// ─── Comments timeline ────────────────────────────────────────────────────────
const CommentsTimeline = ({ surveyId, selected, t, p }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const answersRef = collection(db, 'surveys', surveyId, 'answers');
      let q;
      if (selected !== 'all') {
        const [anio, month] = selected.split('_');
        q = query(answersRef,
          where('anio',  '==', parseInt(anio, 10)),
          where('month', '==', parseInt(month, 10)));
      } else {
        q = answersRef;
      }
      const snap   = await getDocs(q);
      const result = [];
      snap.forEach((d) => {
        const data = d.data();
        const comm = (data.comm ?? '').toString().trim();
        if (comm) result.push({ id: d.id, comm, createdBy: data.createdBy ?? '', addRec: data.addRec ?? null, anio: data.anio, month: data.month });
      });
      result.sort((a, b) => (b.addRec?.toMillis?.() ?? 0) - (a.addRec?.toMillis?.() ?? 0));
      setComments(result);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  }, [surveyId, selected]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  return (
    <Fade in timeout={500}>
      <Box sx={{
        border: p.commentBoxBorder, borderRadius: '14px', background: p.commentBoxBg,
        px: { xs: 1.5, sm: 3 }, py: 2,
        maxHeight: { xs: 420, sm: 560 }, overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(108,99,255,0.35)', borderRadius: 2 },
      }}>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} sx={{ color: '#6C63FF' }} thickness={3} />
          </Box>
        )}
        {!loading && comments.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
            <ChatBubbleOutlineIcon sx={{ color: p.isDark ? '#3A3F58' : '#C5C8D8', fontSize: 38 }} />
            <Typography color="text.secondary" variant="body2">{t.noComments}</Typography>
          </Box>
        )}
        {!loading && comments.length > 0 && (
          <Box sx={{ position: 'relative' }}>
            <Box sx={{
              position: 'absolute', left: { xs: 18, sm: 22 }, top: 0, bottom: 0, width: 2,
              background: p.timelineLineGrad, borderRadius: 99,
            }} />
            {comments.map((c, i) => {
              const color = stringColor(c.createdBy);
              return (
                <Fade key={c.id} in timeout={400} style={{ transitionDelay: `${i * 40}ms` }}>
                  <Stack direction="row" spacing={{ xs: 1.2, sm: 2 }} mb={i < comments.length - 1 ? 2.5 : 0} sx={{ position: 'relative' }}>
                    <Box sx={{ flexShrink: 0, zIndex: 1 }}>
                      <Avatar sx={{
                        width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 },
                        background: `${color}33`, border: `2px solid ${color}66`,
                        color, fontSize: '0.7rem', fontWeight: 700, fontFamily: "'Outfit', sans-serif",
                      }}>
                        {uidInitials(c.createdBy)}
                      </Avatar>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{
                        background: p.commentBubbleBg, border: p.commentBubbleBorder,
                        borderRadius: '4px 12px 12px 12px',
                        px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.4 },
                        transition: 'background 0.15s',
                        '&:hover': { background: p.commentBubbleHover },
                      }}>
                        <Typography variant="body2" sx={{
                          lineHeight: 1.6, wordBreak: 'break-word',
                          fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: p.commentText,
                        }}>
                          {c.comm}
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={0.6} mt={0.5} ml={0.5}>
                        <AccessTimeIcon sx={{ fontSize: 11, color: p.timeColor }} />
                        <Typography variant="caption" sx={{ color: p.timeColor, fontSize: '0.65rem' }}>
                          {formatDate(c.addRec)}
                        </Typography>
                        {selected === 'all' && (
                          <Chip label={`${t.months[c.month] ?? c.month} ${c.anio}`} size="small"
                            sx={{ height: 14, fontSize: '0.58rem', background: p.miscChipBg, color: 'text.secondary', ml: 0.5 }} />
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Fade>
              );
            })}
          </Box>
        )}
      </Box>
    </Fade>
  );
};

// ─── Comment status banner ────────────────────────────────────────────────────
const CommentStatusBanner = ({ enabled, surveyId, selected, t, p }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box mt={5}>
      <Divider sx={{ borderColor: p.dividerColor, mb: 3 }} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        mb={open && enabled ? 2 : 0}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            background: enabled ? 'rgba(67,233,123,0.12)' : 'rgba(255,107,107,0.1)',
            border: enabled ? '1px solid rgba(67,233,123,0.3)' : '1px solid rgba(255,107,107,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {enabled
              ? <CheckCircleIcon sx={{ color: '#43E97B', fontSize: 22 }} />
              : <CancelIcon sx={{ color: '#FF6B6B', fontSize: 22 }} />
            }
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontSize: '0.95rem', color: enabled ? '#43E97B' : '#FF6B6B' }}>
              {enabled ? t.commentsEnabled : t.commentsDisabled}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'none', letterSpacing: 0 }}>
              {enabled ? t.commentsEnabledDesc : t.commentsDisabledDesc}
            </Typography>
          </Box>
        </Stack>
        {enabled && (
          <Button
            variant={open ? 'contained' : 'outlined'} color="primary" size="small"
            startIcon={<CommentIcon />} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setOpen((prev) => !prev)}
            sx={{ flexShrink: 0, borderRadius: 3, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem' }}
          >
            {open ? t.hideComments : t.showComments}
          </Button>
        )}
      </Stack>
      {enabled && (
        <Collapse in={open} unmountOnExit>
          <CommentsTimeline surveyId={surveyId} selected={selected} t={t} p={p} />
        </Collapse>
      )}
    </Box>
  );
};

// ─── Theme toggle ─────────────────────────────────────────────────────────────
const ThemeToggle = ({ mode, onToggle, t, p }) => (
  <Tooltip title={mode === 'light' ? t.toggleDark : t.toggleLight} placement="left">
    <IconButton
      onClick={onToggle} size="small"
      sx={{
        width: 36, height: 36, borderRadius: '10px',
        background: p.toggleBg, color: p.toggleColor,
        border: `1px solid ${p.toggleColor}30`,
        transition: 'all 0.25s',
        '&:hover': { background: p.toggleHover, transform: 'rotate(20deg) scale(1.08)' },
      }}
    >
      {mode === 'light'
        ? <DarkModeRoundedIcon sx={{ fontSize: 18 }} />
        : <WbSunnyRoundedIcon  sx={{ fontSize: 18 }} />
      }
    </IconButton>
  </Tooltip>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const ResultChart = ({ surveyId }) => {
  const t = useTranslations();

  const [themeMode, setThemeMode] = useState('light');
  const toggleMode = useCallback(() => setThemeMode((m) => m === 'light' ? 'dark' : 'light'), []);

  const theme = useMemo(() => buildTheme(themeMode), [themeMode]);
  const p     = useMemo(() => getPalette(themeMode), [themeMode]);

  const [surveyQuestions, setSurveyQuestions] = useState(null);
  const [surveyMeta, setSurveyMeta]           = useState({ title: '', description: '' });
  const [showComment, setShowComment]         = useState(false);
  const [sumByPeriod, setSumByPeriod]         = useState({});
  const [allPeriods, setAllPeriods]           = useState([]);
  const [selected, setSelected]               = useState(currentPeriodKey());
  const [sortMode, setSortMode]               = useState('orden');
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState('');

  useEffect(() => {
    if (!surveyId) return;
    const fetchAll = async () => {
      try {
        const surveySnap = await getDoc(doc(db, 'surveys', surveyId));
        if (!surveySnap.exists()) { setError(t.errorSurvey); setLoading(false); return; }
        const surveyData = surveySnap.data();
        setSurveyQuestions(surveyData.questions || {});
        setShowComment(surveyData.showComment === true);
        setSurveyMeta({ title: surveyData.title ?? '', description: surveyData.description ?? '' });

        const sumSnap  = await getDocs(collection(db, 'surveys', surveyId, 'answerSum'));
        const byPeriod = {};
        sumSnap.docs.forEach((d) => {
          const data   = d.data();
          const period = periodKeyFromData(data);
          if (!byPeriod[period]) byPeriod[period] = [];
          byPeriod[period].push(data);
        });
        const sorted = Object.keys(byPeriod).sort((a, b) => b.localeCompare(a));
        setSumByPeriod(byPeriod);
        setAllPeriods(sorted);
        const cur = currentPeriodKey();
        if (!byPeriod[cur] && sorted.length > 0) setSelected(sorted[0]);
      } catch (err) {
        console.error(err);
        setError(t.errorGeneric);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [surveyId]);

  // Si solo existe un período, no tiene sentido quedarse en "all": se fija al único disponible.
  useEffect(() => {
    if (allPeriods.length === 1 && selected === 'all') {
      setSelected(allPeriods[0]);
    }
  }, [allPeriods, selected]);

  const filteredDocs = useMemo(() => {
    if (selected === 'all') return Object.values(sumByPeriod).flat();
    return sumByPeriod[selected] || [];
  }, [sumByPeriod, selected]);

  const groups = useMemo(() => {
    if (!surveyQuestions) return [];
    const base = aggregateGroups(surveyQuestions, filteredDocs);
    if (sortMode === 'result') {
      return [...base].sort((a, b) => {
        if (a.type === 'text' && b.type !== 'text') return  1;
        if (b.type === 'text' && a.type !== 'text') return -1;
        const da = dominantAnswer(a.counts);
        const db = dominantAnswer(b.counts);
        if (da !== db) return da - db;
        return a.orden - b.orden;
      });
    }
    return base;
  }, [surveyQuestions, filteredDocs, sortMode]);

  const { totalResponses, globalAvg } = useMemo(() => {
    const supported = groups.filter((g) => g.type !== 'text');
    const maxTotal  = supported.length ? Math.max(...supported.map((g) => g.total)) : 0;
    const avgs      = supported
      .map((g) => { const a = weightedAvg(g.counts, g.total); return a ? parseFloat(a) : null; })
      .filter(Boolean);
    const globalAvg = avgs.length ? (avgs.reduce((s, n) => s + n, 0) / avgs.length).toFixed(2) : null;
    return { totalResponses: maxTotal, globalAvg };
  }, [groups]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
      `}</style>

      <Box sx={{
        minHeight: '100vh',
        background: p.pageBg,
        px: { xs: 1.5, sm: 3, md: 4 },
        py: { xs: 2.5, sm: 4 },
        transition: 'background 0.35s ease',
      }}>

        {/* Header */}
        <Fade in timeout={400}>
          <Box mb={3.5}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px', flexShrink: 0, mt: 0.3,
                  background: p.headerLogoBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(108,99,255,0.4)',
                }}>
                  <BarChartIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{
                    fontSize: { xs: '1.15rem', sm: '1.45rem' }, color: p.commentText,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {surveyMeta.title || t.surveyTitle}
                  </Typography>
                  {surveyMeta.description && (
                    <Typography variant="body2" color="text.secondary"
                      sx={{ mt: 0.3, fontSize: '0.82rem', lineHeight: 1.5 }}>
                      {surveyMeta.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'none', letterSpacing: 0, fontSize: '0.7rem', opacity: 0.6 }}>
                    ID: {surveyId}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ flexShrink: 0, mt: 0.3 }}>
                <ThemeToggle mode={themeMode} onToggle={toggleMode} t={t} p={p} />
              </Box>
            </Stack>
          </Box>
        </Fade>

        {/* Loading */}
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh" gap={2}>
            <CircularProgress sx={{ color: '#6C63FF' }} size={46} thickness={3} />
            <Typography color="text.secondary">{t.loading}</Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ borderRadius: 3, mt: 4 }}>{error}</Alert>
        )}

        {!loading && !error && allPeriods.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh" gap={2}>
            <Typography sx={{ fontSize: '3rem' }}>📭</Typography>
            <Typography color="text.secondary">{t.noData}</Typography>
          </Box>
        )}

        {!loading && !error && allPeriods.length > 0 && (
          <>
            <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={3}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FilterBar periods={allPeriods} selected={selected} onChange={setSelected} t={t} />
              </Box>
              <Box sx={{ flexShrink: 0, pt: 0.4 }}>
                <SortBar sortMode={sortMode} onChange={setSortMode} t={t} p={p} />
              </Box>
            </Stack>

            <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} mb={3}>
              <StatCard icon={<PeopleAltIcon sx={{ fontSize: 18 }} />}   label={t.statResponses} value={totalResponses}  color="#6C63FF" delay={0}   p={p} />
              <StatCard icon={<StarRoundedIcon sx={{ fontSize: 18 }} />} label={t.statAverage}   value={globalAvg ?? '—'} color="#F9C74F" delay={100} p={p} />
              <StatCard icon={<QuizIcon sx={{ fontSize: 18 }} />}        label={t.statQuestions} value={groups.length}   color="#43E97B" delay={200} p={p} />
            </Stack>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {groups.map((group, i) => (
                <Grid item xs={12} sm={6} lg={4} key={group.orden}>
                  <QuestionResult group={group} index={i} delay={i * 60} t={t} p={p} />
                </Grid>
              ))}
            </Grid>

            <CommentStatusBanner
              enabled={showComment}
              surveyId={surveyId}
              selected={selected}
              t={t}
              p={p}
            />
          </>
        )}

      </Box>
    </ThemeProvider>
  );
};

export default ResultChart;